import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter, switchMap} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import {Deserialize} from 'cerialize';
import {ConsommationList} from '../../models/rt2012/consommation-list';
import {ReleveIndividuel} from '../../models/rt2012/ReleveIndividuel';
import {Consommation} from '../../models/rt2012/consommation';
import { format } from 'date-fns';

@Injectable({
  providedIn: 'root'
})
export class ConsommationsService {
  baseApiUrl: string = environment.api.baseUrl;

  protected _consommations: BehaviorSubject<Consommation[]> = new BehaviorSubject(null);
  public readonly consommations: Observable<Consommation[]> = this._consommations.asObservable().pipe(filter(c => c != null));

  protected _relevesIndividuels: BehaviorSubject<ReleveIndividuel[]> = new BehaviorSubject(null);
  public readonly relevesIndividuels: Observable<ReleveIndividuel[]> = this._relevesIndividuels.asObservable().pipe(filter(c => c != null));

  protected _lastConsommationsByEnergie: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public readonly lastConsommationsByEnergie: Observable<any> = this._lastConsommationsByEnergie.asObservable();

  constructor(private http: HttpClient) {

  }

  getAll(logementId: number, from: Date, to: Date, type: string): Observable<Consommation[]> {
    return this.http.get(`${this.baseApiUrl}rt2012/detail-consommation/logement/${logementId}/${format(from, 'yyyy-MM-dd')}/${format(to, 'yyyy-MM-dd')}?type=${type}`)
      .pipe(switchMap(data => {
          const consommationList = Deserialize(data, ConsommationList);
          this._consommations.next(consommationList.consommations);
          return this.consommations;
        })
      );
  }

  getLastByEnergie(logementId: number): Observable<any> {
    return this.http.get(`${this.baseApiUrl}rt2012/releve-consommation/last-releves-by-energie/${logementId}`)
      .pipe(switchMap(data => {
          this._lastConsommationsByEnergie.next(data);
          return this.lastConsommationsByEnergie;
        })
      );
  }

  create(releve: ReleveIndividuel): Promise<ReleveIndividuel> {
    return this.http.post(`${this.baseApiUrl}rt2012/releve/individuel/ajouter/${releve.logement.id}/${releve.energie}`, releve).toPromise().then(
      (rawReleve) => {
        return Deserialize(rawReleve, ReleveIndividuel);
      }, (data) => {
        if (data.status === 400 || data.status === 422) {
          return data.error;
        }
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
      });
  }

  delete(releve: ReleveIndividuel): Promise<any> {
    return this.http.delete(`${this.baseApiUrl}rt2012/releve-individuels/${releve.id}`).toPromise().then(
      (data) => {
        return data;
      },
      (data) => {
        if (data.status === 400) {
          return data.error;
        }
        throw new Error('An unexpected error occurred while data loading : ' + data);
      });
  }

  getReleveIndividuels(logementId: number, from: Date, to: Date): Observable<ReleveIndividuel[]> {
    return this.http.get(`${this.baseApiUrl}rt2012/releve-individuels/${logementId}/${format(from, 'yyyy-MM-dd')}/${format(to, 'yyyy-MM-dd')}`)
      .pipe(switchMap(data => {
          const releveList = Deserialize(data, ReleveIndividuel) as ReleveIndividuel[];
          releveList.sort((a, b) => {
            if (a.date > b.date) {
              return -1;
            } else if (a.date < b.date) {
              return 1;
            } else {
              return 0;
            }
          });
          this._relevesIndividuels.next(releveList);
          return this.relevesIndividuels;
        })
      );
  }

  update(releve: ReleveIndividuel): Promise<ReleveIndividuel> {
    return this.http.post(`${this.baseApiUrl}rt2012/releve/individuel/modifier/${releve.logement.id}/${releve.energie}`, releve).toPromise().then(
      (rawReleve) => {
        return Deserialize(rawReleve, ReleveIndividuel);
      }, (data) => {
        if (data.status === 400 || data.status === 422) {
          return data.error;
        }
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
      });
  }
}
